import React, { useCallback, useState } from "react";
import { defineMessages, FormattedMessage } from "react-intl";
import { defaultFormMessages } from "../shared/crud/form";
import { defaultListMessages } from "../shared/crud/list";
import styled from "styled-components";
import { SectionHeading } from "../../components/section-heading";
import { Alert, Button, Card } from "react-bootstrap";
import { useDropzone } from "react-dropzone";
import { AxiosError } from "axios";
import { FullWidthLoader } from "../../components/full-width-loader";
import { useBackofficeClient } from "../../lib/http";
import Highlight from "react-highlight";
import { withAuthorizationVerifier } from "../../components/authorization-verifier";
import { IPropertyLoyaltyBrand, useLoyaltyBrand } from "../../components/loyalty-brand-provider";

export enum LoyaltyCustomerActionType {
  IMPORT = "IMPORT",
  REMOVE_OR_ANONYMIZE = "REMOVE_OR_ANONYMIZE",
}

export interface LoyaltyCustomerManagerProps {
  action: LoyaltyCustomerActionType;
}

interface OperationError {
  message: string;
}

export enum CustomerActionStatus {
  CREATED = "CREATED",
  REMOVED = "REMOVED",
  ANONYMIZED = "ANONYMIZED",
  ERROR = "ERROR",
}
export interface CustomerActionResult {
  rowId: number;
  email: string;
  status: CustomerActionStatus;
  errorMessage?: string;
}

export interface CustomerActionResponseDto {
  message: string;
  errors: CustomerActionResult[];
  results: CustomerActionResult[];
}

const messages = {
  ...defaultFormMessages,
  ...defaultListMessages,
  ...defineMessages({
    createTitle: {
      id: "internalmenu.users.title.batchCreate",
      defaultMessage: "Create users with excel file",
    },
    removeTitle: {
      id: "internalmenu.users.title.batchEdit",
      defaultMessage: "Edit users with excel file",
    },
    description: {
      id: "inventoryMultiUpdate.description",
      defaultMessage: "Update inventory",
    },
    validate: {
      id: "loyaltyCustomerManager.validate",
      defaultMessage: "Validate",
    },
    template: {
      id: "loyaltyCustomerManager.template",
      defaultMessage: "Template",
    },
    selectedBrand: {
      id: "loyaltyCustomerManager.selectedBrand",
      defaultMessage: "Selected brand",
    },
    import: {
      id: "inventoryMultiUpdate.import",
      defaultMessage: "Import",
    },
    exportToExcel: {
      id: "userManager.exportToExcel",
      defaultMessage: "Download template",
    },
    errorsFound: {
      id: "userManager.errorsFound",
      defaultMessage: "rows with errors were found",
    },
    rawResult: {
      id: "userManager.rawResult",
      defaultMessage: "Raw result",
    },
    processedSuccessfully: {
      id: "userManager.processedSuccessfully",
      defaultMessage: "Processed successfully",
    },
    row: {
      id: "userManager.row",
      defaultMessage: "Row",
    },
    reason: {
      id: "userManager.reason",
      defaultMessage: "Reason",
    },
    failed: {
      id: "userManager.failed",
      defaultMessage: "failed",
    },
    createdUsers: {
      id: "userManager.createdUsers",
      defaultMessage: "Created users",
    },
    removedUsers: {
      id: "loyaltyCustomerManager.removedUsers",
      defaultMessage: "Removed users",
    },
    asyncProcessing: {
      id: "userManager.asyncProcessing",
      defaultMessage: "Processed asynchronously",
    },
    password: {
      id: "userManager.password",
      defaultMessage: "Password",
    },
    user: {
      id: "userManager.user",
      defaultMessage: "User",
    },
    dropHere: {
      id: "app.imageManager.dropHere",
      defaultMessage: "Drop files here...",
    },
  }),
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin: 1rem;
  > * {
    margin: 1rem 0;
  }
`;
const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: 1rem;
  > * {
    margin: 0 5px;
  }
  :last-child {
    margin-right: 5px;
  }
`;

const DropzoneContainer = styled.div`
  width: auto;
  height: 100px;
  border: 2px dashed #dae6e8;
  background-color: ${({ theme }) => theme.greyBackground};
  font-size: 21px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  margin-bottom: 25px;
  outline: none;
`;

const ResultContainer = styled.div`
  margin: 16px;
`;

const StyledHighlight = styled(Highlight)`
  font-size: 12px;
`;

const HighlightContainer = styled.div`
  display: flex;
  justify-content: center;
  background-color: #f7f7f7;
  padding: 20px;
`;

const StyledTable = styled.table`
  border-collapse: collapse;
  width: 100%;
  td,
  th {
    border: 1px solid #ddd;
    padding: 8px;
  }
  tr:nth-child(even) {
    background-color: #f2f2f2;
  }
  tr:hover {
    background-color: #ddd;
  }
  th {
    padding-top: 12px;
    padding-bottom: 12px;
    text-align: left;
    background-color: #04aa6d;
    color: white;
  }
`;

type ImportGoal = "VALIDATE" | "REMOVE" | "CREATE";
function UserManagerResultRenderer({
  action,
  result,
}: {
  action: LoyaltyCustomerActionType;
  result: CustomerActionResponseDto;
}) {
  if (!result.results.length && !result.errors.length) {
    return null;
  }
  return (
    <>
      <div>
        <Alert variant="dark">
          <strong>
            <div> Summary:</div>
            <div>
              {result.results.length}{" "}
              {action === LoyaltyCustomerActionType.IMPORT ? (
                <FormattedMessage {...messages.createdUsers} />
              ) : (
                <FormattedMessage {...messages.removedUsers} />
              )}
            </div>
            <div>
              {result.errors.length} <FormattedMessage {...messages.errorsFound} />
            </div>
          </strong>
        </Alert>
      </div>
      {result.errors.length ? (
        <>
          <Alert variant="danger">
            <strong>
              {result.errors.length} <FormattedMessage {...messages.errorsFound} />:
            </strong>
          </Alert>
          <StyledTable>
            <thead>
              <tr>
                <th>Row #</th>
                <th>Email</th>
                <th>Error Message</th>
              </tr>
            </thead>
            <tbody>
              {result.errors.map(err => (
                <tr>
                  <td>{err.rowId}</td>
                  <td>{err.email}</td>
                  <td>{err.errorMessage}</td>
                </tr>
              ))}
            </tbody>
          </StyledTable>
        </>
      ) : null}
      {result.results.length ? (
        <>
          <Alert variant="info">
            <strong>
              {result.results.length}{" "}
              {action === LoyaltyCustomerActionType.IMPORT ? (
                <FormattedMessage {...messages.createdUsers} />
              ) : (
                <FormattedMessage {...messages.removedUsers} />
              )}
              :
            </strong>
          </Alert>
          <StyledTable>
            <thead>
              <tr>
                <th>Row #</th>
                <th>Email</th>
              </tr>
            </thead>
            <tbody>
              {result.results.map(err => (
                <tr>
                  <td>{err.rowId}</td>
                  <td>{err.email}</td>
                </tr>
              ))}
            </tbody>
          </StyledTable>
        </>
      ) : null}
    </>
  );
}

function ValidateResult({ result }: { result: CustomerActionResponseDto }) {
  return (
    <div>
      {result.errors.length ? (
        <Alert variant="danger">
          <strong>
            {result.errors.length} <FormattedMessage {...messages.errorsFound} />:
          </strong>
        </Alert>
      ) : (
        <Alert variant="success">
          <strong>No errors found!</strong>
        </Alert>
      )}
      {result.errors.length ? (
        <StyledTable>
          <thead>
            <tr>
              <th>Row #</th>
              <th>Email</th>
              <th>Error Message</th>
            </tr>
          </thead>
          <tbody>
            {result.errors.map(err => (
              <tr>
                <td>{err.rowId}</td>
                <td>{err.email}</td>
                <td>{err.errorMessage}</td>
              </tr>
            ))}
          </tbody>
        </StyledTable>
      ) : null}
    </div>
  );
}

function LoyaltyCustomerManager({ action }: LoyaltyCustomerManagerProps) {
  const loyaltyBrand: IPropertyLoyaltyBrand | undefined | null = useLoyaltyBrand();
  const backofficeClient = useBackofficeClient();
  const [file, setFile] = useState<File | undefined>(undefined);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [goal, setGoal] = useState<ImportGoal | undefined>(undefined);
  const [error, setError] = useState<OperationError | null | undefined>(undefined);
  const [result, setResult] = useState<CustomerActionResponseDto | undefined>(undefined);
  const onDrop = useCallback(<T extends File>(acceptedFiles: T[]) => {
    if (!acceptedFiles || acceptedFiles.length === 0) {
      return;
    }
    setFile(acceptedFiles[0]);
  }, []);
  const { getRootProps, getInputProps } = useDropzone({ onDrop });

  const handleImport = useCallback(
    async (goal: ImportGoal) => {
      if (!file) {
        return;
      }
      // Both actions are proxied by backoffice-manager for the selected brand: the removal file
      // may only name customers of that brand.
      if (!loyaltyBrand?.ticker) {
        return;
      }
      if (goal === "REMOVE" && action !== LoyaltyCustomerActionType.REMOVE_OR_ANONYMIZE) {
        throw new Error("Incompatible params");
      }
      const formData = new FormData();
      formData.append("file", file);
      setLoading(true);
      setError(undefined);
      setGoal(undefined);
      try {
        const headers = {
          "content-type": "multipart/form-data",
          "backoffice-ng-cookie": document.cookie,
        };
        if (action === LoyaltyCustomerActionType.IMPORT) {
          const isOnlyValidate = goal === "VALIDATE" ? "true" : "false";
          const response = await backofficeClient.post<FormData, CustomerActionResponseDto>(
            `/v1/loyalty/proxy/${loyaltyBrand?.ticker}/customers/file?isOnlyValidate=${isOnlyValidate}`,
            formData,
            { headers }
          );
          setResult(response);
        } else {
          const response = await backofficeClient.delete<CustomerActionResponseDto>(
            `/v1/loyalty/proxy/${loyaltyBrand?.ticker}/customers/remove/file`,
            { data: formData, headers }
          );
          setResult(response);
        }
      } catch (err) {
        if (err instanceof AxiosError && err.response) {
          setError({ message: err.response.data?.message ?? "Could not process file" });
        } else {
          setError({ message: "Unexpected error" });
        }
      }
      setLoading(false);
      setGoal(goal);
    },
    [action, backofficeClient, file, loyaltyBrand?.ticker]
  );

  if (isLoading) {
    return <FullWidthLoader />;
  }
  if (!loyaltyBrand?.ticker) {
    return <div>Can only be used with engines with a brand</div>;
  }

  return (
    <>
      <SectionHeading>
        <FormattedMessage
          {...(action === LoyaltyCustomerActionType.IMPORT
            ? messages.createTitle
            : messages.removeTitle)}
        />
      </SectionHeading>
      <Card className="ml-4 mr-4 mb-4 mt-4">
        <Container>
          <div>
            <div>
              Selected brand: <b>{loyaltyBrand.ticker}</b>
            </div>
            <FormattedMessage {...messages.description} />
            <div style={{ fontWeight: "bold" }}>
              <a
                target="_blank"
                href={
                  action === LoyaltyCustomerActionType.IMPORT
                    ? "https://docs.google.com/spreadsheets/d/1ZIv1yQYp7I3-cIxHpuDoMeMG6tcGWKp2v7wNCj0jsqQ"
                    : "https://docs.google.com/spreadsheets/d/1TzEzV9jmSGyu3QfMOyjZktV2pt1Xv2nGMDerLSK2W38"
                }
                rel="noreferrer">
                Template
              </a>
            </div>
          </div>
          <DropzoneContainer {...getRootProps()}>
            <input {...getInputProps()} />
            {file ? <div>{file.name}</div> : <FormattedMessage {...messages.dropHere} />}
          </DropzoneContainer>
        </Container>
        {error && (
          <Alert variant="danger">
            <strong>
              <FormattedMessage {...messages.saveError} />
              {": "}
              <span>{error.message}</span>
            </strong>
          </Alert>
        )}
        {action === LoyaltyCustomerActionType.IMPORT ? (
          <>
            <div style={{ display: "flex" }}>
              <ButtonContainer>
                <Button variant="primary" type="button" onClick={() => handleImport("VALIDATE")}>
                  <FormattedMessage {...messages.validate} />
                </Button>
              </ButtonContainer>

              <ButtonContainer>
                <Button variant="primary" type="button" onClick={() => handleImport("CREATE")}>
                  <FormattedMessage {...messages.import} />
                </Button>
              </ButtonContainer>
            </div>
          </>
        ) : (
          <>
            <ButtonContainer>
              <Button variant="primary" type="button" onClick={() => handleImport("REMOVE")}>
                <FormattedMessage {...messages.import} />
              </Button>
            </ButtonContainer>
          </>
        )}

        {result && (
          <ResultContainer>
            <Alert variant="success">
              <strong>
                <FormattedMessage {...messages.processedSuccessfully} />
              </strong>
            </Alert>
            {goal !== "VALIDATE" ? (
              <UserManagerResultRenderer action={action} result={result} />
            ) : (
              <ValidateResult result={result} />
            )}
            <hr />
            <div>
              <FormattedMessage {...messages.rawResult} />:
            </div>
            <HighlightContainer>
              <StyledHighlight className="json">{JSON.stringify(result, null, 2)}</StyledHighlight>
            </HighlightContainer>
          </ResultContainer>
        )}
      </Card>
    </>
  );
}

export default withAuthorizationVerifier(LoyaltyCustomerManager);
